import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import SingleEvent from '../../classes/events/SingleEvent';
import './EventLikeButton.css';

function EventLikeButton({ eventId }) {
  const { t } = useTranslation('events');
  const [likesCount, setLikesCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadLikeData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Pobierz aktualnego użytkownika
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Pobierz liczbę polubień i status polubienia użytkownika równolegle
      const [countResult, userLiked] = await Promise.all([
        SingleEvent.loadLikesCount(eventId),
        user?.id ? SingleEvent.isLikedByUser(eventId, user.id) : Promise.resolve(false)
      ]);

      setLikesCount(countResult.count || 0);
      setIsLiked(userLiked);
    } catch (error) {
      console.error('Error loading like data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadLikeData();
  }, [eventId, loadLikeData]);

  const handleToggleLike = async () => {
    if (!currentUser) {
      // Można tutaj dodać przekierowanie do logowania
      return;
    }

    if (isToggling) return;

    try {
      setIsToggling(true);
      
      // Optymistyczna aktualizacja UI
      const newLiked = !isLiked;
      const newCount = newLiked ? likesCount + 1 : likesCount - 1;
      setIsLiked(newLiked);
      setLikesCount(newCount);

      // Wykonaj operację w bazie danych
      const result = await SingleEvent.toggleLike(eventId, currentUser.id);
      
      if (result.error) {
        // Cofnij optymistyczną aktualizację w przypadku błędu
        setIsLiked(!newLiked);
        setLikesCount(likesCount);
        console.error('Error toggling like:', result.error);
      } else {
        // Zaktualizuj stan na podstawie wyniku z bazy danych
        setIsLiked(result.liked);
        setLikesCount(result.count);
      }
    } catch (error) {
      // Cofnij optymistyczną aktualizację w przypadku błędu
      setIsLiked(!isLiked);
      setLikesCount(likesCount);
      console.error('Error toggling like:', error);
    } finally {
      setIsToggling(false);
    }
  };

  if (isLoading) {
    return (
      <button 
        className="event-like-button event-like-button-loading" 
        type="button"
        disabled
      >
        <span className="event-like-icon">❤️</span>
        <span className="event-like-count">...</span>
      </button>
    );
  }

  return (
    <button 
      className={`event-like-button ${isLiked ? 'event-like-button-liked' : ''} ${isToggling ? 'event-like-button-toggling' : ''}`}
      onClick={handleToggleLike}
      type="button"
      title={isLiked ? t('events.page.unlike') : t('events.page.like')}
      disabled={isToggling || !currentUser}
    >
      <span className="event-like-icon">{isLiked ? '❤️' : '🤍'}</span>
      <span className="event-like-count">{likesCount}</span>
    </button>
  );
}

export default EventLikeButton;
